import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../contexts/APIContext';
import logger from '../utils/logger';
import { getPersonalizedStoryDescription } from "../utils/storyDescription";

const RecommendedStories = ({ onClose, currentStoryId = null }) => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const [recommendedStories, setRecommendedStories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determine if this is being used as a sidebar (no onClose) or modal (has onClose)
  const isSidebar = !onClose;

  useEffect(() => {
    const fetchRecommendedStories = async () => {
      if (!apiClient) return;

      try {
        setLoading(true);
        const response = await apiClient.getStoriverseStories();
        
        if (response.status && response.data && response.data.length > 0) {
          // Filter out current story if provided, then get up to 6 random stories
          let stories = response.data;
          if (currentStoryId) {
            stories = stories.filter(story => story.id !== currentStoryId);
          }
          
          // Shuffle and get up to 6 stories
          const shuffled = [...stories].sort(() => 0.5 - Math.random());
          const selected = shuffled.slice(0, 6);
          setRecommendedStories(selected);
        } else {
          setRecommendedStories([]);
        }
      } catch (error) {
        logger.error('Failed to fetch recommended stories:', error);
        setRecommendedStories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendedStories();
  }, [apiClient, currentStoryId, isSidebar]);

  const handleStoryClick = (storyId) => {
    sessionStorage.setItem("autoplay_video_story", "true");
    navigate(`/stories/${storyId}`);
    onClose?.();
  };

  // Sidebar mode (used in StoryDetail)
  if (isSidebar) {
    return (
      <div className="flex flex-col">
        <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 18, fontWeight: 700, color: "#0065F8", marginBottom: 14, paddingLeft: 4 }}>
          Recommended for you
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "rgba(0,101,248,0.2)", borderTopColor: "#0065F8" }}></div>
          </div>
        ) : recommendedStories.length === 0 ? (
          <p style={{ color: "#334155", fontSize: 14, textAlign: "center", padding: "48px 8px" }}>
            No other videos available
          </p>
        ) : (
          <div
            className="overflow-y-auto space-y-2 pr-1"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0,101,248,0.2) transparent',
              maxHeight: 'calc(100vh - 200px)'
            }}
          >
            {recommendedStories.map((story) => {
              const storyIdToNavigate = story.id;

              return (
                <button
                  key={storyIdToNavigate || story.id}
                  onClick={() => handleStoryClick(storyIdToNavigate)}
                  className="w-full group flex gap-3 p-2 text-left transition-all duration-150"
                  style={{ borderRadius: 14, background: "transparent" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,101,248,0.04)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 relative">
                    {story.thumbnail_url ? (
                      <div className="w-[130px] h-[82px] overflow-hidden" style={{ borderRadius: 12, background: "#EAF3FF" }}>
                        <img
                          src={story.thumbnail_url}
                          alt={story.name || "Video"}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          onError={(e) => { e.target.src = "/assets/pixley-panda-nobg.png"; }}
                        />
                      </div>
                    ) : (
                      <div className="w-[130px] h-[82px] flex items-center justify-center" style={{ borderRadius: 12, background: "linear-gradient(135deg, #EAF3FF, #E1EBFF)" }}>
                        <span className="text-3xl">🎬</span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-start pt-1">
                    <h3 className="line-clamp-2 mb-1" style={{ fontSize: 14, fontWeight: 700, color: "#0B1A33", lineHeight: 1.3 }}>
                      {story.name || story.title || "Untitled Video"}
                    </h3>
                    {story.description && (
                      <p className="line-clamp-1" style={{ fontSize: 12, color: "#334155" }}>
                        {getPersonalizedStoryDescription(story)}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Modal mode (used in VideoPlayer overlay)
  if (loading) {
    return (
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="max-w-6xl w-full mx-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-white/10 p-8 shadow-2xl">
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  if (recommendedStories.length === 0) {
    return null;
  }

  return (
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto">
      <div className="max-w-6xl w-full mx-4 my-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-white/10 p-8 shadow-2xl">
        {/* Header */}
        <div className="mb-6">
          <h3 className="text-3xl font-bold text-white text-center mb-2">
            Recommended for you
          </h3>
          <p className="text-gray-300 text-center">
            Discover more amazing stories
          </p>
        </div>

        {/* Stories Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          {recommendedStories.map((story) => (
            <button
              key={story.id}
              onClick={() => handleStoryClick(story.id)}
              className="group relative bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-400/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20"
            >
              {/* Story Cover */}
              <div className="aspect-video relative bg-gradient-to-br from-purple-500/30 to-blue-500/30 overflow-hidden">
                {story.thumbnail_url ? (
                  <img
                    src={story.thumbnail_url}
                    alt={story.name || 'Story cover'}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-4xl opacity-50">📖</span>
                  </div>
                )}
                {/* Play Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-6 h-6 text-purple-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Story Info */}
              <div className="p-3">
                <h4 className="text-white font-semibold text-sm mb-1 line-clamp-2 text-left">
                  {story.name || 'Untitled Story'}
                </h4>
                {story.description && (
                  <p className="text-gray-400 text-xs line-clamp-2 text-left">
                    {getPersonalizedStoryDescription(story)}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Close Button */}
        <div className="flex justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="px-6 py-3 text-gray-400 hover:text-white transition-colors duration-200 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecommendedStories;

